import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { useConsent, CHAT_DELETED_EVENT } from "../hooks/useConsent";
import { LegalSheet } from "./LegalSheet";

// ─────────────────────────────────────────────
// ZENITH TOKENS
// ─────────────────────────────────────────────
const Z = {
  primary:             "#55624d",
  primaryFixed:        "#d9e7cd",
  primaryFixedDim:     "#bdcbb2",
  onPrimary:           "#ffffff",
  surface:             "#f8faf3",
  surfaceContainer:    "#ecefe8",
  surfaceContainerLow: "#f2f4ed",
  surfaceContainerLowest: "#ffffff",
  onSurface:           "#191c18",
  onSurfaceVariant:    "#444841",
  secondary:           "#755754",
  secondaryContainer:  "#fed7d2",
  onSecondaryContainer:"#795b58",
  outline:             "#757870",
  outlineVariant:      "#c5c8be",
  error:               "#ba1a1a",
  errorContainer:      "#ffdad6",
};

// ─────────────────────────────────────────────
// AVATAR FARBEN
// ─────────────────────────────────────────────
const AVATAR_COLORS = [
  { bg: "#55624d", text: "#ffffff", label: "Moos" },
  { bg: "#755754", text: "#ffffff", label: "Terrakotta" },
  { bg: "#605f56", text: "#ffffff", label: "Stein" },
  { bg: "#3b7dd8", text: "#ffffff", label: "Himmel" },
  { bg: "#7c4dbc", text: "#ffffff", label: "Lavendel" },
  { bg: "#c0714f", text: "#ffffff", label: "Bernstein" },
  { bg: "#4a9e8a", text: "#ffffff", label: "Jade" },
  { bg: "#b5483c", text: "#ffffff", label: "Rubin" },
  { bg: "#2e312d", text: "#ffffff", label: "Nacht" },
  { bg: "#d9e7cd", text: "#2f3c29", label: "Salbei" },
];

function getInitials(name) {
  if (!name) return "?";
  return name.trim().split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

// ─────────────────────────────────────────────
// SHARED UI COMPONENTS
// ─────────────────────────────────────────────
function Toggle({ value, onToggle, disabled }) {
  return (
    <div
      onClick={disabled ? undefined : onToggle}
      style={{
        width: 44, height: 24, borderRadius: 12, position: "relative",
        background: value ? Z.primary : Z.outlineVariant,
        transition: "background 0.2s",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        flexShrink: 0,
      }}
    >
      <div style={{
        position: "absolute", top: 3,
        left: value ? 23 : 3,
        width: 18, height: 18, borderRadius: "50%",
        background: "#fff", transition: "left 0.2s",
        boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
      }} />
    </div>
  );
}

function Row({ icon, label, sublabel, value, onClick, danger, toggleValue, onToggle, disabled }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%", display: "flex", alignItems: "center",
        justifyContent: "space-between", padding: "13px 16px",
        borderRadius: 12, border: "none", background: "transparent",
        cursor: onClick ? "pointer" : "default",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}
      onMouseEnter={(e) => { if (onClick) e.currentTarget.style.background = Z.surfaceContainerLow; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 18, width: 24, textAlign: "center" }}>{icon}</span>
        <div>
          <span style={{ fontSize: 14, fontWeight: 500, color: danger ? Z.error : Z.onSurface, display: "block" }}>{label}</span>
          {sublabel && <span style={{ fontSize: 11, color: Z.outline }}>{sublabel}</span>}
        </div>
      </div>
      {value !== undefined && !onToggle && (
        <span style={{ fontSize: 13, color: Z.outline }}>{value}</span>
      )}
      {onToggle && (
        <Toggle value={toggleValue} onToggle={(e) => { e?.stopPropagation?.(); onToggle(); }} disabled={disabled} />
      )}
      {onClick && !onToggle && <span style={{ color: Z.outline, fontSize: 16 }}>›</span>}
    </button>
  );
}

function SectionLabel({ label }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: Z.outline, letterSpacing: "0.08em", textTransform: "uppercase", padding: "16px 16px 4px", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {label}
    </div>
  );
}

function Divider() {
  return <div style={{ height: 1, background: Z.outlineVariant, margin: "4px 16px" }} />;
}

function TimeInput({ value, onChange, disabled }) {
  return (
    <input
      type="time"
      value={value}
      onChange={onChange}
      disabled={disabled}
      style={{
        padding: "5px 10px", borderRadius: 8,
        border: `1px solid ${Z.outlineVariant}`,
        fontSize: 13, color: Z.onSurface,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: Z.surfaceContainerLowest,
        outline: "none", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    />
  );
}

function Feedback({ msg }) {
  if (!msg?.text) return null;
  return (
    <div style={{ margin: "0 16px", padding: "10px 14px", borderRadius: 10, fontSize: 13, lineHeight: 1.5, fontFamily: "'Plus Jakarta Sans', sans-serif", background: msg.type === "success" ? Z.primaryFixed : msg.type === "info" ? "#e8f4fd" : Z.errorContainer, color: msg.type === "success" ? Z.primary : msg.type === "info" ? "#1d4ed8" : Z.error }}>
      {msg.text}
    </div>
  );
}

// ─────────────────────────────────────────────
// NOTIFICATION SECTION
// ─────────────────────────────────────────────
function NotificationSection({ userId }) {
  const {
    supported, permission, isActive, loading,
    requestPermission, updateSettings, unsubscribe,
  } = usePushNotifications(userId);

  // Lokale Einstellungen (gespiegelt aus localStorage + Supabase)
  const [morningEnabled, setMorningEnabled] = useState(
    () => localStorage.getItem("habitOS_morningNotif") !== "false"
  );
  const [nightEnabled, setNightEnabled] = useState(
    () => localStorage.getItem("habitOS_nightNotif") !== "false"
  );
  const [morningTime, setMorningTime] = useState(
    () => localStorage.getItem("habitOS_morningTime") || "07:00"
  );
  const [nightTime, setNightTime] = useState(
    () => localStorage.getItem("habitOS_nightTime") || "21:00"
  );
  const [msg, setMsg] = useState(null);

  // Einstellungen nach Supabase syncen (debounced)
  useEffect(() => {
    if (!isActive) return;
    const t = setTimeout(() => {
      updateSettings({ morningEnabled, morningTime, nightEnabled, nightTime });
    }, 600);
    return () => clearTimeout(t);
  }, [morningEnabled, morningTime, nightEnabled, nightTime, isActive]);

  const handleMainToggle = async () => {
    setMsg(null);
    if (isActive) {
      // Deaktivieren
      await unsubscribe();
      setMsg({ type: "info", text: "Push-Benachrichtigungen deaktiviert." });
    } else {
      if (!supported) {
        setMsg({ type: "error", text: "Dein Browser unterstützt keine Push-Benachrichtigungen." });
        return;
      }
      try {
        await requestPermission({ morningEnabled, morningTime, nightEnabled, nightTime });
        setMsg({ type: "success", text: "Push-Benachrichtigungen aktiviert! ✓" });
      } catch (err) {
        if (err.message.includes("verweigert") || permission === "denied") {
          setMsg({ type: "error", text: "Berechtigung verweigert. Bitte in den Browser-Einstellungen aktivieren." });
        } else {
          setMsg({ type: "error", text: err.message });
        }
      }
    }
  };

  const handleMorningToggle = () => {
    const v = !morningEnabled;
    setMorningEnabled(v);
    localStorage.setItem("habitOS_morningNotif", String(v));
  };

  const handleNightToggle = () => {
    const v = !nightEnabled;
    setNightEnabled(v);
    localStorage.setItem("habitOS_nightNotif", String(v));
  };

  const handleMorningTime = (e) => {
    setMorningTime(e.target.value);
    localStorage.setItem("habitOS_morningTime", e.target.value);
  };

  const handleNightTime = (e) => {
    setNightTime(e.target.value);
    localStorage.setItem("habitOS_nightTime", e.target.value);
  };

  return (
    <>
      <SectionLabel label="Benachrichtigungen" />

      {/* Haupt-Toggle */}
      <Row
        icon={isActive ? "🔔" : "🔕"}
        label="Push-Benachrichtigungen"
        sublabel={
          !supported ? "Nicht unterstützt in diesem Browser" :
          permission === "denied" ? "In Browser-Einstellungen blockiert" :
          isActive ? "Aktiv — Erinnerungen werden gesendet" :
          "Inaktiv"
        }
        toggleValue={isActive}
        onToggle={handleMainToggle}
        disabled={loading || !supported}
      />

      <Feedback msg={msg} />

      {/* Morning Reminder */}
      {isActive && (
        <>
          <div style={{ padding: "4px 16px 4px 52px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${Z.outlineVariant}` }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 500, color: Z.onSurface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  ☀️ Morning Reminder
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <TimeInput
                  value={morningTime}
                  onChange={handleMorningTime}
                  disabled={!morningEnabled}
                />
                <Toggle value={morningEnabled} onToggle={handleMorningToggle} />
              </div>
            </div>

            {/* Night Reminder */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${Z.outlineVariant}` }}>
              <div>
                <span style={{ fontSize: 14, fontWeight: 500, color: Z.onSurface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  🌙 Night Reminder
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <TimeInput
                  value={nightTime}
                  onChange={handleNightTime}
                  disabled={!nightEnabled}
                />
                <Toggle value={nightEnabled} onToggle={handleNightToggle} />
              </div>
            </div>
          </div>

          {/* Info */}
          <div style={{ margin: "4px 16px 8px", padding: "10px 14px", background: Z.surfaceContainerLow, borderRadius: 10, fontSize: 12, color: Z.outline, lineHeight: 1.5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            ℹ️ Benachrichtigungen werden in deiner lokalen Zeitzone ({Intl.DateTimeFormat().resolvedOptions().timeZone}) gesendet. Änderungen werden automatisch gespeichert.
          </div>
        </>
      )}
    </>
  );
}

// ─────────────────────────────────────────────
// PROFIL SHEET
// ─────────────────────────────────────────────
export function ProfileSheet({ user, streak, freezes, subscription, isDark, onToggleDark, lang, onToggleLanguage, onClose, onSignOut }) {
  const [view, setView]           = useState("main");
  const [legal, setLegal]         = useState(null);
  const [displayName, setDisplayName] = useState(user?.user_metadata?.full_name || "");
  const [avatarColor, setAvatarColor] = useState(() => {
    try { return JSON.parse(localStorage.getItem("habitOS_avatarColor")) || AVATAR_COLORS[0]; }
    catch { return AVATAR_COLORS[0]; }
  });
  const [nameInput, setNameInput]   = useState(displayName);
  const [pwNew, setPwNew]           = useState("");
  const [pwConfirm, setPwConfirm]   = useState("");
  const [msg, setMsg]               = useState(null);
  const [loading, setLoading]       = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const { consent, hasConsent, revoke } = useConsent(user?.id);

  const initials = getInitials(displayName || user?.email);

  const handleColorSelect = (color) => {
    setAvatarColor(color);
    localStorage.setItem("habitOS_avatarColor", JSON.stringify(color));
  };

  const handleSaveName = async () => {
    if (!nameInput.trim()) return;
    setLoading(true); setMsg(null);
    try {
      await supabase.auth.updateUser({ data: { full_name: nameInput.trim() } });
      await supabase.from("profiles").update({ display_name: nameInput.trim() }).eq("id", user.id);
      setDisplayName(nameInput.trim());
      setMsg({ type: "success", text: "Name gespeichert." });
      setTimeout(() => setView("main"), 1200);
    } catch { setMsg({ type: "error", text: "Fehler beim Speichern." }); }
    finally { setLoading(false); }
  };

  const handleSavePassword = async () => {
    if (pwNew.length < 8) { setMsg({ type: "error", text: "Mindestens 8 Zeichen." }); return; }
    if (pwNew !== pwConfirm) { setMsg({ type: "error", text: "Passwörter stimmen nicht überein." }); return; }
    setLoading(true); setMsg(null);
    try {
      await supabase.auth.updateUser({ password: pwNew });
      setMsg({ type: "success", text: "Passwort geändert." });
      setPwNew(""); setPwConfirm("");
      setTimeout(() => setView("main"), 1200);
    } catch (e) { setMsg({ type: "error", text: e.message }); }
    finally { setLoading(false); }
  };

  const handleDeleteAccount = async () => {
    if (deleteInput !== "LÖSCHEN") return;
    setLoading(true);
    try {
      await supabase.from("profiles").delete().eq("id", user.id);
      onSignOut();
    } catch (e) { setMsg({ type: "error", text: "Fehler: " + e.message }); }
    finally { setLoading(false); }
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px", borderRadius: 10,
    border: `1px solid ${Z.outlineVariant}`, background: Z.surfaceContainerLowest,
    fontSize: 14, color: Z.onSurface,
    fontFamily: "'Plus Jakarta Sans', sans-serif", outline: "none",
  };
  const primaryBtn = {
    width: "100%", padding: "13px", borderRadius: 12, border: "none",
    background: Z.primary, color: Z.onPrimary,
    fontSize: 14, fontWeight: 600, cursor: "pointer",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    opacity: loading ? 0.7 : 1,
  };

  // ── Unterseiten ───────────────────────────
  if (view === "editName") return (
    <SheetWrapper onClose={onClose}>
      <SubHeader title="Name ändern" onBack={() => { setView("main"); setMsg(null); }} />
      <div style={{ padding: "0 20px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
        <input style={inputStyle} value={nameInput} onChange={(e) => setNameInput(e.target.value)} placeholder="Dein Name" autoFocus />
        <Feedback msg={msg} />
        <button style={primaryBtn} onClick={handleSaveName} disabled={loading}>
          {loading ? "Speichern…" : "Speichern"}
        </button>
      </div>
    </SheetWrapper>
  );

  if (view === "editPassword") return (
    <SheetWrapper onClose={onClose}>
      <SubHeader title="Passwort ändern" onBack={() => { setView("main"); setMsg(null); }} />
      <div style={{ padding: "0 20px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
        <input style={inputStyle} type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} placeholder="Neues Passwort (min. 8 Zeichen)" />
        <input style={inputStyle} type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} placeholder="Passwort bestätigen" />
        <Feedback msg={msg} />
        <button style={primaryBtn} onClick={handleSavePassword} disabled={loading}>
          {loading ? "Speichern…" : "Passwort ändern"}
        </button>
      </div>
    </SheetWrapper>
  );

  const handleRevokeConsent = async () => {
    setLoading(true); setMsg(null);
    try {
      await revoke();
      setMsg({ type: "success", text: "Einwilligung widerrufen. Der KI-Begleiter ist aus." });
      setTimeout(() => { setView("main"); setMsg(null); }, 1500);
    } catch { setMsg({ type: "error", text: "Widerruf hat nicht geklappt. Bitte versuch es noch einmal." }); }
    finally { setLoading(false); }
  };

  const handleDeleteChat = async () => {
    setLoading(true); setMsg(null);
    try {
      const { error } = await supabase.from("agent_messages").delete().eq("user_id", user.id);
      if (error) throw error;
      window.dispatchEvent(new Event(CHAT_DELETED_EVENT));
      setMsg({ type: "success", text: "Chatverlauf gelöscht." });
      setTimeout(() => { setView("main"); setMsg(null); }, 1500);
    } catch { setMsg({ type: "error", text: "Löschen hat nicht geklappt. Bitte versuch es noch einmal." }); }
    finally { setLoading(false); }
  };

  if (view === "revokeConsent") return (
    <SheetWrapper onClose={onClose}>
      <SubHeader title="Einwilligung widerrufen" onBack={() => { setView("main"); setMsg(null); }} />
      <div style={{ padding: "0 20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        <p style={{ fontSize: 13, color: Z.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.6, margin: 0 }}>
          Nach dem Widerruf senden wir nichts mehr an die KI: kein Chat, keine Etikett-Analyse, kein Wochenrückblick und keine Push-Nachrichten des Begleiters. Was bis jetzt verarbeitet wurde, bleibt rechtmäßig. Deinen Chatverlauf kannst du zusätzlich unter „Chatverlauf löschen“ entfernen.
        </p>
        <Feedback msg={msg} />
        <button style={{ ...primaryBtn, background: Z.error }} onClick={handleRevokeConsent} disabled={loading}>
          {loading ? "Wird gespeichert…" : "Einwilligung widerrufen"}
        </button>
      </div>
    </SheetWrapper>
  );

  if (view === "deleteChat") return (
    <SheetWrapper onClose={onClose}>
      <SubHeader title="Chatverlauf löschen" onBack={() => { setView("main"); setMsg(null); }} />
      <div style={{ padding: "0 20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ padding: "14px 16px", background: Z.errorContainer, borderRadius: 12, fontSize: 13, color: Z.error, lineHeight: 1.6 }}>
          ⚠️ Alle Nachrichten mit deinem KI-Begleiter, Etikett-Analysen und Wochenrückblicke werden dauerhaft gelöscht.
        </div>
        <Feedback msg={msg} />
        <button style={{ ...primaryBtn, background: Z.error }} onClick={handleDeleteChat} disabled={loading}>
          {loading ? "Wird gelöscht…" : "Chatverlauf endgültig löschen"}
        </button>
      </div>
    </SheetWrapper>
  );

  if (view === "deleteConfirm") return (
    <SheetWrapper onClose={onClose}>
      <SubHeader title="Konto löschen" onBack={() => { setView("main"); setMsg(null); }} />
      <div style={{ padding: "0 20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ padding: "14px 16px", background: Z.errorContainer, borderRadius: 12, fontSize: 13, color: Z.error, lineHeight: 1.6 }}>
          ⚠️ Diese Aktion ist unwiderruflich. Alle deine Daten werden dauerhaft gelöscht.
        </div>
        <p style={{ fontSize: 13, color: Z.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.6 }}>
          Tippe <strong>LÖSCHEN</strong> um zu bestätigen:
        </p>
        <input style={{ ...inputStyle, borderColor: deleteInput === "LÖSCHEN" ? Z.error : Z.outlineVariant }}
          value={deleteInput} onChange={(e) => setDeleteInput(e.target.value)} placeholder="LÖSCHEN" />
        <Feedback msg={msg} />
        <button style={{ ...primaryBtn, background: deleteInput === "LÖSCHEN" ? Z.error : Z.outlineVariant }}
          onClick={handleDeleteAccount} disabled={deleteInput !== "LÖSCHEN" || loading}>
          {loading ? "Wird gelöscht…" : "Konto endgültig löschen"}
        </button>
      </div>
    </SheetWrapper>
  );

  // ── Main View ─────────────────────────────
  return (
    <SheetWrapper onClose={onClose}>

      {/* Avatar + Name */}
      <div style={{ padding: "8px 20px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: avatarColor.bg, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}>
          <span style={{ fontSize: 26, fontWeight: 700, color: avatarColor.text, fontFamily: "'Manrope', sans-serif" }}>
            {initials}
          </span>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: Z.onSurface, fontFamily: "'Manrope', sans-serif" }}>{displayName || "Kein Name"}</div>
          <div style={{ fontSize: 13, color: Z.outline, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{user?.email}</div>
        </div>
        {/* Farb-Picker */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center", marginTop: 4 }}>
          {AVATAR_COLORS.map((color) => (
            <button key={color.label} onClick={() => handleColorSelect(color)} title={color.label}
              style={{ width: 28, height: 28, borderRadius: "50%", border: "2px solid", borderColor: avatarColor.bg === color.bg ? Z.onSurface : "transparent", background: color.bg, cursor: "pointer", padding: 0, boxShadow: avatarColor.bg === color.bg ? `0 0 0 2px ${Z.surface}, 0 0 0 4px ${color.bg}` : "none", transition: "box-shadow 0.15s" }}
            />
          ))}
        </div>
      </div>

      <Divider />

      {/* Fortschritt */}
      <SectionLabel label="Dein Fortschritt" />
      <div style={{ padding: "8px 16px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        {[
          { icon: "🔥", value: streak, label: "Streak" },
          { icon: "❄️", value: freezes, label: "Freezes" },
          { icon: subscription === "pro" ? "⭐" : "🌱", value: subscription === "pro" ? "Pro" : "Free", label: "Plan" },
        ].map(({ icon, value, label }) => (
          <div key={label} style={{ background: Z.surfaceContainerLow, borderRadius: 14, padding: "14px 10px", textAlign: "center", border: `1px solid ${Z.outlineVariant}` }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: Z.onSurface, fontFamily: "'Manrope', sans-serif" }}>{value}</div>
            <div style={{ fontSize: 11, color: Z.outline, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600 }}>{label}</div>
          </div>
        ))}
      </div>

      <Divider />

      {/* Profil */}
      <SectionLabel label="Profil" />
      <Row icon="✏️" label="Name ändern" onClick={() => { setNameInput(displayName); setView("editName"); }} />
      <Row icon="📧" label="E-Mail" value={user?.email?.length > 22 ? user.email.slice(0, 22) + "…" : user?.email} />

      <Divider />

      {/* Benachrichtigungen — vollständig verdrahtet */}
      <NotificationSection userId={user?.id} />

      <Divider />

      {/* Einstellungen */}
      <SectionLabel label="Einstellungen" />
      <Row
        icon={isDark ? "🌙" : "☀️"}
        label="Dark Mode"
        sublabel={isDark ? "Dunkles Design aktiv" : "Helles Design aktiv"}
        toggleValue={isDark}
        onToggle={onToggleDark}
      />
      <Row
        icon="🌍"
        label="Sprache / Language"
        sublabel={lang === "de" ? "Deutsch" : "English"}
        value={lang === "de" ? "DE" : "EN"}
        onClick={onToggleLanguage}
      />

      <Divider />

      {/* Account */}
      <SectionLabel label="Datenschutz" />
      <Row
        icon="✦"
        label="KI-Begleiter: Einwilligung"
        sublabel={hasConsent
          ? `Erteilt am ${new Date(consent.granted_at).toLocaleDateString("de-DE")}`
          : "Nicht erteilt – im Begleiter-Tab möglich"}
        onClick={hasConsent ? () => { setMsg(null); setView("revokeConsent"); } : undefined}
      />
      <Row icon="🧹" label="Chatverlauf löschen" danger onClick={() => { setMsg(null); setView("deleteChat"); }} />
      <Row icon="📄" label="Datenschutzerklärung" onClick={() => setLegal("datenschutz")} />
      <Row icon="🏷️" label="Impressum" onClick={() => setLegal("impressum")} />

      <Divider />

      <SectionLabel label="Account" />
      {subscription !== "pro" && <Row icon="⭐" label="Auf Pro upgraden" onClick={() => {}} />}
      <Row icon="🔑" label="Passwort ändern" onClick={() => { setMsg(null); setView("editPassword"); }} />
      <Row icon="🚪" label="Abmelden" onClick={onSignOut} />
      <Row icon="🗑️" label="Konto löschen" danger onClick={() => { setDeleteInput(""); setMsg(null); setView("deleteConfirm"); }} />

      <div style={{ height: 32 }} />
      {legal && <LegalSheet Z={Z} kind={legal} onClose={() => setLegal(null)} />}
    </SheetWrapper>
  );
}

// ─────────────────────────────────────────────
// WRAPPER-KOMPONENTEN
// ─────────────────────────────────────────────
function SheetWrapper({ onClose, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(25,28,24,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      <div style={{ background: Z.surface, width: "100%", maxHeight: "92vh", borderRadius: "24px 24px 0 0", overflowY: "auto", display: "flex", flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>
        {/* Handle + Close Button */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 4px", flexShrink: 0 }}>
          <div style={{ width: 32 }} />
          <div style={{ width: 36, height: 4, borderRadius: 2, background: Z.outlineVariant }} />
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: "50%",
              border: "none",
              background: Z.surfaceContainerHigh,
              color: Z.onSurfaceVariant,
              fontSize: 16, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SubHeader({ title, onBack }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 20px 16px" }}>
      <button onClick={onBack} style={{ border: "none", background: "none", fontSize: 14, color: Z.onSurfaceVariant, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0 }}>←</button>
      <h2 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 18, fontWeight: 500, color: Z.onSurface, margin: 0 }}>{title}</h2>
    </div>
  );
}
